import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBulkUpdatePartType } from "#modules/quoteRequestLines/view/useBulkUpdatePartType";
import { getSelectablePartTypeOptions, iconForPartType } from "#shared/helpers/partTypeOptions";
import { Icon } from "#shared/icons/Icon";
import { useAnalytics } from "#shared/useAnalytics";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { SelectInput } from "#shared/view/designSystem/SelectInput";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import { AssemblyWithChildren, PartTypeSchema, PartTypeType, QuoteRequestLineDto } from "shared";

type Props = {
  selectedQuoteRequestLines: QuoteRequestLineDto[];
  assemblies: AssemblyWithChildren<QuoteRequestLineDto>[];
  disabled?: boolean;
  disabledTooltip?: string;
  size?: "sm" | "md";
};

export const BulkPartTypeSelect = ({
  selectedQuoteRequestLines,
  assemblies,
  disabled = false,
  disabledTooltip: disabledTooltipOverride,
  size = "sm",
}: Props) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { bulkUpdatePartType } = useBulkUpdatePartType();
  const [selectedPartType, setSelectedPartType] = useState<string | null>(null);

  const hasSomeAssemblySelected = useMemo(
    () =>
      assemblies.some((assembly) => {
        return (
          assembly.quoteRequestLine.part.type === PartTypeSchema.enum.ASSEMBLY &&
          selectedQuoteRequestLines.some((qrl) => qrl.id === assembly.quoteRequestLine.id)
        );
      }),
    [assemblies, selectedQuoteRequestLines],
  );

  const isDisabled = disabled || hasSomeAssemblySelected;
  const disabledTooltip = hasSomeAssemblySelected
    ? t("quoteRequestLine.bulkPartTypeAssemblyTooltip")
    : disabledTooltipOverride;

  const options = useMemo(() => getSelectablePartTypeOptions(), []);

  const handlePartTypeChange = (value: string | null) => {
    if (!value || selectedQuoteRequestLines.length === 0) {
      return;
    }

    const partType = value as PartTypeType;

    analytics.capture("batch_action:part_type_changed", {
      selected_count: selectedQuoteRequestLines.length,
      part_type: partType,
    });

    setSelectedPartType(null);

    bulkUpdatePartType({
      quoteRequestLines: selectedQuoteRequestLines,
      partType,
      partSubType: null,
    });
  };

  return (
    <Tooltip label={disabledTooltip} disabled={!disabledTooltip} multiline w={300}>
      <Box
        component="span"
        display="inline-block"
        style={{ cursor: isDisabled ? "not-allowed" : undefined }}
      >
        <SelectInput
          placeholder={t("quoteRequestLine.bulkPartTypePlaceholder")}
          value={selectedPartType}
          data={options}
          disabled={isDisabled}
          size={size}
          miw={200}
          searchable
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          renderOption={({ option }) => (
            <Group gap="xxs">
              <Icon name={iconForPartType[option.value as PartTypeType]} size={12} />
              {option.label}
            </Group>
          )}
          onChange={handlePartTypeChange}
        />
      </Box>
    </Tooltip>
  );
};
